import { bindActionCreators } from '@reduxjs/toolkit';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { openFilter, openFoldout } from '../../actions/filters';
import { search } from '../../actions/search';

import ActiveFilters from './active-filters';
import FilterFoldout from './filter-foldout';

import { useLocation, useNavigate } from 'react-router-dom';
import type { Facet } from '../../@types/ElasticSearchResponse';
import { getListQueryConcatQueryfilter } from '../../helpers/list';
import type QueryFilter from '../../helpers/query-filter';
import { getQueryFilter } from '../../utils/reselectors';
import FilterBar from './filter-bar';

const BASE_SEARCH_PATH = '/sok';

interface DefaultFilterBlockProps {
  target: string;
  silent?: boolean;
  umbrellaFacet?: string;
  useContainer?: boolean;
  children?: React.ReactNode;
  navigate?: (queryFilter: QueryFilter) => void;
}

const DefaultFilterBlock = ({
  target,
  silent,
  umbrellaFacet,
  useContainer,
  children,
  navigate,
}: DefaultFilterBlockProps) => {
  const dispatch = useDispatch();
  const _navigate = useNavigate();

  const { pathname } = useLocation();

  const queryFilter = useSelector(getQueryFilter);

  const actions = bindActionCreators({ search, openFoldout, openFilter }, dispatch);

  const localNavigate = (nextQueryFilter: QueryFilter) => {
    if (!silent) {
      if (target && pathname !== BASE_SEARCH_PATH) {
        _navigate(`${target}${getListQueryConcatQueryfilter(nextQueryFilter.toQueryURL())}`);
      } else if (nextQueryFilter.toQueryString() !== queryFilter.toQueryString()) {
        _navigate(
          `${location.pathname}${getListQueryConcatQueryfilter(nextQueryFilter.toQueryURL())}`,
          { replace: true },
        );
      }
    }

    if (!queryFilter.isSame(queryFilter) && silent) {
      _navigate(
        `${target || BASE_SEARCH_PATH}${getListQueryConcatQueryfilter(queryFilter.toQueryURL())}`,
      );
    }
  };

  const onRequestSearch = (nextQueryFilter: QueryFilter) => {
    const _nextQueryFilter = nextQueryFilter.removePagination();

    if (navigate) {
      navigate(_nextQueryFilter);
    } else {
      localNavigate(_nextQueryFilter);
    }

    actions.search(_nextQueryFilter);
  };

  const onRequestFilter = (facet: Facet) => {
    if (navigate) {
      navigate(queryFilter);
    } else {
      localNavigate(queryFilter);
    }

    actions.openFilter(facet, !process.env.SSR ? window.scrollY : 0, false);
  };

  const onRequestFoldout = () => {
    if (navigate) {
      navigate(queryFilter);
    } else {
      localNavigate(queryFilter);
    }

    actions.openFoldout(!process.env.SSR ? window.scrollY : 0);
  };

  if (useContainer) {
    return (
      <>
        <div className="container m-b">
          <ActiveFilters onRequestSearch={onRequestSearch} umbrellaFacet={umbrellaFacet} />
          <FilterBar
            onRequestSearch={onRequestSearch}
            onRequestFilter={onRequestFilter}
            onRequestFoldout={onRequestFoldout}
          />
        </div>
        {children}
        <div className="m-b">
          <div className="container">
            <FilterFoldout onRequestSearch={onRequestSearch} />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <ActiveFilters onRequestSearch={onRequestSearch} umbrellaFacet={umbrellaFacet} />
      <FilterBar
        onRequestSearch={onRequestSearch}
        onRequestFilter={onRequestFilter}
        onRequestFoldout={onRequestFoldout}
      />
      {children}
      <FilterFoldout onRequestSearch={onRequestSearch} />
    </>
  );
};

export default DefaultFilterBlock;
